/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import API, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  EnvelopeSimple, ArrowSquareIn, ArrowSquareOut, Paperclip, ArrowsClockwise, Calendar,
  ArrowBendUpLeft, ArrowBendDoubleUpLeft, ArrowBendUpRight, Trash, PaperPlaneTilt, DotsThreeVertical,
  ArrowsLeftRight, ClockClockwise, ArrowsOut, ArrowsIn,
} from "@phosphor-icons/react";
import ComposeEmailDialog from "@/components/app/ComposeEmailDialog";
import EmailBatchCleanupPanel from "@/components/app/EmailBatchCleanupPanel";

/**
 * "Communications" tab on the case page. Lists every email sent from
 * ConveyTrack on this case + every Outlook message matched to the case's
 * participants (pulled on demand via the Sync button).
 *
 * Each row shows direction (↗/↘), from/to, subject, preview, attachments,
 * and the source mode (Outlook / mock). Clicking a row opens a read-only
 * modal with the full HTML body.
 */
export default function CommunicationsTab({ caseId, isSolicitor, caseClients = [], caseParties = [], caseReferrer = null, legacyClientEmail = "", propertyAddress = "" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [openItem, setOpenItem] = useState(null);
  const [detailMax, setDetailMax] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeMode, setComposeMode] = useState("new"); // "new" | "reply" | "replyAll" | "forward"
  const [replyTo, setReplyTo] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);  // {item, scope: "both"|"local"}
  const [deleting, setDeleting] = useState(false);
  const [moveItem, setMoveItem] = useState(null);   // email row being moved
  const [moveQuery, setMoveQuery] = useState("");
  const [moveResults, setMoveResults] = useState([]);
  const [moving, setMoving] = useState(false);
  // Triage history — chronological feed of Mark-replied / No-reply /
  // Undo events on this case. Loaded lazily when the user expands the
  // collapsible above the email list.
  const [triageHistory, setTriageHistory] = useState([]);
  const [triageOpen, setTriageOpen] = useState(false);
  const [triageLoading, setTriageLoading] = useState(false);
  useEffect(() => {
    if (!caseId || !isSolicitor || !triageOpen) return;
    setTriageLoading(true);
    API.get(`/cases/${caseId}/triage-history`)
      .then(({ data }) => setTriageHistory(Array.isArray(data) ? data : []))
      .catch(() => setTriageHistory([]))
      .finally(() => setTriageLoading(false));
  }, [caseId, isSolicitor, triageOpen]);

  // Unified address book of everyone with an email on this case file —
  // shown in the To/Cc dropdown picker on the composer. Each entry is
  // {email, name, role} where role is "Client" | "Party · {role}" | "Referrer".
  // Built here so the composer doesn't have to know about the case shape.
  const caseAddressBook = useMemo(() => {
    const book = [];
    const seen = new Set();
    const push = (email, name, role) => {
      const e = (email || "").trim().toLowerCase();
      if (!e || seen.has(e)) return;
      seen.add(e);
      book.push({ email: e, name: name || "", role });
    };
    (caseClients || []).forEach((c) => push(c.email, c.name, `Client · ${c.case_role || "client"}`));
    (caseParties || []).forEach((p) => push(p.email, p.name, `Party · ${p.role || "party"}`));
    if (legacyClientEmail) push(legacyClientEmail, "Primary client", "Client");
    if (caseReferrer && caseReferrer.email) push(caseReferrer.email, caseReferrer.name, "Referrer");
    return book;
  }, [caseClients, caseParties, caseReferrer, legacyClientEmail]);
  // Track whether we've already auto-synced this case-page mount to avoid
  // firing repeatedly when the user tabs in/out of Communications.
  const autoSyncedRef = useRef(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/cases/${caseId}/communications`);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load communications");
    } finally {
      setLoading(false);
    }
  }, [caseId]);
  useEffect(() => { if (caseId) fetchAll(); }, [caseId, fetchAll]);

  // Move-to-another-case dialog: debounced server search. We deliberately
  // use the global /api/cases endpoint with a `q` param so the list isn't
  // limited to the cases the solicitor opens regularly.
  useEffect(() => {
    if (!moveItem) return;
    const q = moveQuery.trim();
    const handle = setTimeout(async () => {
      try {
        const { data } = await API.get("/cases", { params: { search: q || undefined, limit: 30 } });
        const rows = Array.isArray(data) ? data : (data?.items || []);
        setMoveResults(rows.filter((c) => c.id !== caseId).slice(0, 30));
      } catch {
        setMoveResults([]);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [moveItem, moveQuery, caseId]);

  // Silent background auto-sync on first open per case-page mount. Solicitor-
  // only (the sync endpoint requires it anyway). We skip toasts here — the
  // list just refreshes when the request lands. If the user explicitly clicks
  // "Sync from Outlook" later they get the verbose toasts.
  useEffect(() => {
    if (!caseId || !isSolicitor || autoSyncedRef.current) return;
    autoSyncedRef.current = true;
    (async () => {
      try {
        await API.post(`/cases/${caseId}/communications/sync`);
        fetchAll();
      } catch (err) {
        // Soft-fail — most common case is "Outlook not connected", which we
        // don't want to nag about on every case open.
      }
    })();
  }, [caseId, isSolicitor, fetchAll]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await API.post(`/cases/${caseId}/communications/sync`);
      const newCount = data.matched_new || 0;
      if (newCount > 0) {
        toast.success(`Synced — ${newCount} new email${newCount === 1 ? "" : "s"} pulled from your Outlook`);
      } else if (data.case_participants_checked === 0) {
        toast.info("No client / party emails on this case yet — add them to start matching inbox emails.");
      } else {
        toast.info("No new matching emails in your Outlook");
      }
      fetchAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sync failed — is your Outlook connected?");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="communications-tab">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Communications</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Every email sent from ConveyTrack on this case, plus any inbox / sent-items emails that match a client or party on this case.
          </p>
        </div>
        {isSolicitor && (
          <div className="flex items-center gap-1.5">
            <Button onClick={() => { setComposeMode("new"); setReplyTo(null); setComposeOpen(true); }} className="h-9 px-3 text-sm gap-1.5 text-white" style={{ background: "#100f5d" }} data-testid="comms-compose-btn">
              <PaperPlaneTilt weight="bold" className="w-4 h-4" />
              Compose
            </Button>
            <Button onClick={handleSync} disabled={syncing} variant="outline" className="h-9 px-3 text-sm gap-1.5" data-testid="comms-sync-btn">
              <ArrowsClockwise weight="bold" className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing…" : "Sync from Outlook"}
            </Button>
          </div>
        )}
      </div>

      {/* Triage history collapsible — solicitor-only audit feed of
          Mark replied / No reply / Undo actions taken on this case. */}
      {isSolicitor && (
        <div className="mb-3 border border-slate-200 rounded-md bg-slate-50" data-testid="comms-triage-panel">
          <button
            type="button"
            onClick={() => setTriageOpen((v) => !v)}
            className="w-full px-3 py-2 flex items-center justify-between text-xs font-medium text-slate-700 hover:bg-slate-100 rounded-md"
            data-testid="comms-triage-toggle"
          >
            <span className="flex items-center gap-1.5">
              <ClockClockwise weight="bold" className="w-3.5 h-3.5 text-slate-500" />
              Triage history
              {triageHistory.length > 0 && (
                <span className="px-1.5 py-0.5 text-[10px] rounded bg-slate-200 text-slate-700 font-semibold">{triageHistory.length}</span>
              )}
            </span>
            <span className="text-slate-400 text-[10px]">{triageOpen ? "Hide" : "Show"}</span>
          </button>
          {triageOpen && (
            <div className="px-3 pb-3 pt-1 border-t border-slate-200">
              {triageLoading ? (
                <p className="text-xs text-slate-400 py-2 text-center">Loading…</p>
              ) : triageHistory.length === 0 ? (
                <p className="text-xs text-slate-400 py-2 text-center">No triage actions on this case yet.</p>
              ) : (
                <ul className="space-y-1.5" data-testid="comms-triage-list">
                  {triageHistory.map((e) => {
                    const map = {
                      email_marked_replied: { label: "Marked replied", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
                      email_marked_no_reply_needed: { label: "No reply needed", cls: "bg-slate-100 text-slate-700 border-slate-200" },
                      email_mark_undone: { label: "Undone", cls: "bg-amber-50 text-amber-700 border-amber-200" },
                    };
                    const s = map[e.action] || { label: e.action, cls: "bg-slate-100 text-slate-600 border-slate-200" };
                    const d = e.details || {};
                    return (
                      <li key={e.id} className="text-[11px] flex items-start gap-2" data-testid={`comms-triage-row-${e.id}`}>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-semibold shrink-0 ${s.cls}`}>
                          {s.label}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="truncate text-slate-700">
                            <span className="font-medium">{e.user_name || "Solicitor"}</span>
                            {d.subject && (
                              <>
                                <span className="text-slate-400"> · </span>
                                <span className="text-slate-600">{d.subject}</span>
                              </>
                            )}
                          </p>
                          {d.from && (
                            <p className="text-[10px] text-slate-400 truncate">from {d.from}{d.received_at ? ` · received ${new Date(d.received_at).toLocaleString("en-GB", { day: "2-digit", month: "short" })}` : ""}</p>
                          )}
                        </div>
                        <span className="text-[10px] text-slate-400 shrink-0">
                          {new Date(e.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {/* Bulk cleanup — detach emails auto-matched by the pre-fix loose
          address key. Solicitor-only. */}
      {isSolicitor && (
        <EmailBatchCleanupPanel caseId={caseId} onDetached={fetchAll} />
      )}

      {loading ? (
        <p className="text-sm text-slate-400 py-8 text-center">Loading…</p>
      ) : items.length === 0 ? (
        <div className="py-12 text-center" data-testid="comms-empty">
          <EnvelopeSimple weight="duotone" className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          <p className="text-sm text-slate-500">No communications yet.</p>
          <p className="text-xs text-slate-400 mt-1">
            Use <span className="font-medium">Generate &amp; Email</span> on the Documents tab to send the first one — or click <span className="font-medium">Sync from Outlook</span> to pull existing emails.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 -mx-2" data-testid="comms-list">
          {items.map((item) => {
            const isIncoming = item.direction === "incoming";
            const when = new Date(item.sent_at);
            const whenLabel = when.toLocaleString("en-GB", {
              day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
            });
            return (
              <li key={item.id} className="group px-2 py-3 hover:bg-slate-50 cursor-pointer transition-colors rounded-md" onClick={() => setOpenItem(item)} data-testid={`comms-row-${item.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isIncoming ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-700"}`}>
                    {isIncoming ? <ArrowSquareIn weight="bold" className="w-4 h-4" /> : <ArrowSquareOut weight="bold" className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${isIncoming ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-700"}`}>
                        {isIncoming ? "Incoming" : "Outgoing"}
                      </span>
                      {item.mode === "mock" && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">Mock</span>
                      )}
                      {(item.has_attachments || (item.attachments && item.attachments.length > 0)) && (
                        <span className="text-[10px] text-slate-400 inline-flex items-center gap-0.5">
                          <Paperclip weight="bold" className="w-3 h-3" />
                          {item.attachments?.length || ""}
                        </span>
                      )}
                      <span className="text-[11px] text-slate-400 inline-flex items-center gap-1">
                        <Calendar weight="bold" className="w-3 h-3" />
                        {whenLabel}
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-slate-900 mt-1 truncate">{item.subject}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                      {isIncoming ? (
                        <>From <span className="font-medium text-slate-700">{item.from_name || item.from_email}</span> · to {item.to_emails?.join(", ") || "—"}</>
                      ) : (
                        <>From <span className="font-medium text-slate-700">{item.from_name || item.from_email}</span> · to {item.to_emails?.join(", ") || "—"}</>
                      )}
                    </p>
                    {item.body_preview && (
                      <p className="text-[11px] text-slate-400 mt-1 line-clamp-2">{item.body_preview}</p>
                    )}
                  </div>
                  {isSolicitor && (
                    <div className="flex items-center gap-0.5 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setComposeMode("reply"); setReplyTo(item); setComposeOpen(true); }}
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900"
                        title="Reply"
                        data-testid={`comms-reply-${item.id}`}
                      >
                        <ArrowBendUpLeft weight="bold" className="w-3.5 h-3.5" />
                      </button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            onClick={(e) => e.stopPropagation()}
                            className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900"
                            title="More actions"
                            data-testid={`comms-more-${item.id}`}
                          >
                            <DotsThreeVertical weight="bold" className="w-3.5 h-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuItem onClick={() => { setComposeMode("replyAll"); setReplyTo(item); setComposeOpen(true); }} data-testid={`comms-reply-all-${item.id}`}>
                            <ArrowBendDoubleUpLeft weight="bold" className="w-3.5 h-3.5 mr-2" /> Reply all
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setComposeMode("forward"); setReplyTo(item); setComposeOpen(true); }} data-testid={`comms-forward-${item.id}`}>
                            <ArrowBendUpRight weight="bold" className="w-3.5 h-3.5 mr-2" /> Forward
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setMoveItem(item); setMoveQuery(""); setMoveResults([]); }} data-testid={`comms-move-${item.id}`}>
                            <ArrowsLeftRight weight="bold" className="w-3.5 h-3.5 mr-2" /> Move to another case
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setConfirmDelete({ item, scope: "local" })}
                            data-testid={`comms-delete-local-${item.id}`}
                            className="text-red-600 focus:text-red-700"
                          >
                            <Trash weight="bold" className="w-3.5 h-3.5 mr-2" /> Delete from ConveyTrack only
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setConfirmDelete({ item, scope: "both" }); }}
                        className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-600"
                        title={item.graph_message_id ? "Delete from ConveyTrack & Outlook" : "Delete from ConveyTrack"}
                        data-testid={`comms-delete-${item.id}`}
                      >
                        <Trash weight="bold" className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Message-detail dialog */}
      <Dialog open={!!openItem} onOpenChange={(o) => !o && setOpenItem(null)}>
        <DialogContent
          className={detailMax
            ? "w-[99vw] max-w-[99vw] h-[98vh] max-h-[98vh] flex flex-col p-0 gap-0"
            : "w-[96vw] sm:w-[92vw] max-w-[1400px] h-[92vh] max-h-[92vh] flex flex-col p-0 gap-0"}
          data-testid="comms-detail-dialog"
        >
          {openItem && (
            <>
              <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-200 shrink-0">
                <div className="flex items-start justify-between gap-2 pr-8">
                  <DialogTitle
                    className="text-lg sm:text-xl break-words"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    {openItem.subject}
                  </DialogTitle>
                  <button
                    type="button"
                    onClick={() => setDetailMax((m) => !m)}
                    className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 text-slate-500 shrink-0"
                    title={detailMax ? "Restore" : "Maximize"}
                    data-testid="comms-detail-maximize-toggle"
                  >
                    {detailMax ? <ArrowsIn weight="bold" className="w-4 h-4" /> : <ArrowsOut weight="bold" className="w-4 h-4" />}
                  </button>
                </div>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto overflow-x-hidden px-6 py-4 space-y-3 text-sm">
                <div className="grid grid-cols-[80px_1fr] gap-x-3 gap-y-1 text-xs min-w-0">
                  <span className="text-slate-500">From</span>
                  <span className="text-slate-900 break-all">{openItem.from_name ? `${openItem.from_name} <${openItem.from_email}>` : openItem.from_email}</span>
                  <span className="text-slate-500">To</span>
                  <span className="text-slate-900 break-all">{openItem.to_emails?.join(", ")}</span>
                  {openItem.cc_emails?.length > 0 && (
                    <>
                      <span className="text-slate-500">Cc</span>
                      <span className="text-slate-900 break-all">{openItem.cc_emails.join(", ")}</span>
                    </>
                  )}
                  <span className="text-slate-500">Date</span>
                  <span className="text-slate-900">{new Date(openItem.sent_at).toLocaleString("en-GB")}</span>
                </div>
                {openItem.attachments?.length > 0 && (
                  <div className="border border-slate-200 rounded-lg p-3 bg-slate-50" data-testid="comms-detail-attachments">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1">
                      <Paperclip weight="bold" className="w-3 h-3" /> {openItem.attachments.length} attachment{openItem.attachments.length === 1 ? "" : "s"}
                    </p>
                    <ul className="space-y-1">
                      {openItem.attachments
                        .filter((a) => !a.is_inline)
                        .map((a, i) => {
                          // Outlook-sourced attachments carry an `id` from
                          // Graph; we stream them via the case-comms attachment
                          // endpoint. Locally-uploaded attachments (no
                          // graph id) just show their filename — the case
                          // documents tab is the source of truth for those.
                          const isDownloadable = !!(a.id && openItem.graph_message_id);
                          return (
                            <li key={a.id || i} className="text-xs text-slate-700 flex items-center justify-between gap-3" data-testid={`comms-attachment-${a.id || i}`}>
                              <span className="truncate">
                                <span>{a.filename}</span>
                                {a.size > 0 && (
                                  <span className="text-slate-400 ml-1.5">{(a.size / 1024).toFixed(1)} KB</span>
                                )}
                              </span>
                              {isDownloadable && (
                                <div className="flex items-center gap-2 shrink-0">
                                  <button
                                    type="button"
                                    onClick={async () => {
                                    try {
                                      const resp = await API.get(
                                        `/cases/${caseId}/communications/${openItem.id}/attachments/${encodeURIComponent(a.id)}/download`,
                                        { responseType: "blob" },
                                      );
                                      const blob = new Blob([resp.data]);
                                      const url = URL.createObjectURL(blob);
                                      const link = document.createElement("a");
                                      link.href = url;
                                      link.download = a.filename || "attachment";
                                      document.body.appendChild(link);
                                      link.click();
                                      link.remove();
                                      setTimeout(() => URL.revokeObjectURL(url), 1000);
                                    } catch (err) {
                                      // Surface the backend's actual `detail`
                                      // string so solicitors (and support)
                                      // can see WHY the download failed
                                      // rather than a generic message.
                                      // axios with responseType=blob puts the
                                      // error payload in `err.response.data`
                                      // AS A Blob, so we have to text-decode
                                      // it before we can parse the JSON.
                                      let detail = "";
                                      const status = err?.response?.status || "";
                                      try {
                                        const raw = err?.response?.data;
                                        if (raw && typeof raw.text === "function") {
                                          const txt = await raw.text();
                                          try {
                                            detail = JSON.parse(txt)?.detail || txt;
                                          } catch {
                                            detail = txt;
                                          }
                                        } else if (typeof raw === "string") {
                                          detail = raw;
                                        } else if (raw?.detail) {
                                          detail = raw.detail;
                                        }
                                      } catch { /* fall through */ }
                                      toast.error(
                                        `Attachment download failed${status ? ` (${status})` : ""}${detail ? `: ${detail}` : ""}`,
                                        { duration: 8000 },
                                      );
                                    }
                                  }}
                                    className="text-blue-600 hover:underline font-medium"
                                    data-testid={`comms-attachment-download-${a.id || i}`}
                                  >
                                    Download
                                  </button>
                                  {isSolicitor && (
                                    <button
                                      type="button"
                                      onClick={async () => {
                                        try {
                                          const { data } = await API.post(
                                            `/cases/${caseId}/communications/${openItem.id}/attachments/${encodeURIComponent(a.id)}/save-to-documents`,
                                          );
                                          toast.success(
                                            `Saved "${data?.document?.original_filename || a.filename}" to Documents`,
                                            { duration: 6000 },
                                          );
                                        } catch (err) {
                                          const detail = err?.response?.data?.detail || "";
                                          const status = err?.response?.status || "";
                                          toast.error(
                                            `Save to Documents failed${status ? ` (${status})` : ""}${detail ? `: ${detail}` : ""}`,
                                            { duration: 8000 },
                                          );
                                        }
                                      }}
                                      className="text-slate-500 hover:text-slate-900 hover:underline"
                                      data-testid={`comms-attachment-save-${a.id || i}`}
                                      title="Save this attachment permanently to the case Documents tab. Protects against the sender deleting the email later."
                                    >
                                      Save to Docs
                                    </button>
                                  )}
                                </div>
                              )}
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                )}
                <div
                  className="prose prose-sm max-w-none overflow-x-auto border border-slate-200 rounded-lg p-4 bg-white email-body-viewer"
                  data-testid="comms-detail-body"
                  // The body is solicitor-authored or trusted Outlook content for
                  // a case the user already has access to; we surface it as-is.
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: openItem.body_html || `<p class="text-slate-400 italic">No body content.</p>` }}
                />
              </div>
              {/* Action footer — Reply / Reply all / Forward / Delete. Sticky
                  to the bottom of the dialog so it's always reachable on long
                  emails. Solicitor-only (clients can't see this dialog). */}
              {isSolicitor && (
                <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 shrink-0 flex items-center justify-between gap-2 flex-wrap" data-testid="comms-detail-footer">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      size="sm"
                      onClick={() => {
                        setComposeMode("reply");
                        setReplyTo(openItem);
                        setComposeOpen(true);
                        setOpenItem(null);
                      }}
                      className="h-8 px-3 text-white"
                      style={{ background: "#100f5d" }}
                      data-testid="comms-detail-reply-btn"
                    >
                      <ArrowBendUpLeft weight="bold" className="w-3.5 h-3.5 mr-1.5" />
                      Reply
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setComposeMode("replyAll");
                        setReplyTo(openItem);
                        setComposeOpen(true);
                        setOpenItem(null);
                      }}
                      className="h-8 px-3"
                      data-testid="comms-detail-reply-all-btn"
                    >
                      <ArrowBendDoubleUpLeft weight="bold" className="w-3.5 h-3.5 mr-1.5" />
                      Reply all
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setComposeMode("forward");
                        setReplyTo(openItem);
                        setComposeOpen(true);
                        setOpenItem(null);
                      }}
                      className="h-8 px-3"
                      data-testid="comms-detail-forward-btn"
                    >
                      <ArrowBendUpRight weight="bold" className="w-3.5 h-3.5 mr-1.5" />
                      Forward
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    {openItem.graph_message_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setConfirmDelete({ item: openItem, scope: "local" });
                          setOpenItem(null);
                        }}
                        className="h-8 px-3 text-slate-700"
                        title="Remove from ConveyTrack only (Outlook copy stays)"
                        data-testid="comms-detail-remove-btn"
                      >
                        Remove from case
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setConfirmDelete({ item: openItem, scope: "full" });
                        setOpenItem(null);
                      }}
                      className="h-8 px-3 text-red-700 border-red-200 hover:bg-red-50"
                      data-testid="comms-detail-delete-btn"
                    >
                      <Trash weight="bold" className="w-3.5 h-3.5 mr-1.5" />
                      Delete
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <ComposeEmailDialog
        caseId={caseId}
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        onSent={fetchAll}
        replyTo={replyTo}
        mode={composeMode}
        prefillClients={caseClients}
        propertyAddress={propertyAddress}
        addressBook={caseAddressBook}
      />

      <Dialog open={!!moveItem} onOpenChange={(o) => !o && !moving && setMoveItem(null)}>
        <DialogContent className="max-w-lg" data-testid="comms-move-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>Move email to another case</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-1">Search by title, address or client. The email will move off this case and onto the one you pick.</p>
          <input
            autoFocus
            value={moveQuery}
            onChange={(e) => setMoveQuery(e.target.value)}
            placeholder="Search cases…"
            className="w-full h-10 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-300"
            data-testid="comms-move-search"
          />
          <div className="max-h-72 overflow-y-auto border border-slate-100 rounded-md">
            {moveResults.length === 0 ? (
              <p className="p-4 text-xs text-slate-400 text-center">{moveQuery ? "No matching cases" : "Start typing to search…"}</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {moveResults.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      disabled={moving}
                      onClick={async () => {
                        if (!moveItem) return;
                        setMoving(true);
                        try {
                          const { data } = await API.post(`/cases/${caseId}/communications/${moveItem.id}/move`, { target_case_id: c.id });
                          setItems((arr) => arr.filter((m) => m.id !== moveItem.id));
                          const rerouted = Number(data?.rerouted_thread_ids) || 0;
                          if (rerouted > 0) {
                            toast.success(`Moved to "${c.title || c.property_address || c.id.slice(0, 8)}". Future replies in this thread will auto-file there.`);
                          } else {
                            toast.success(`Moved to "${c.title || c.property_address || c.id.slice(0, 8)}"`);
                          }
                          setMoveItem(null);
                        } catch (err) {
                          toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Move failed");
                        } finally {
                          setMoving(false);
                        }
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 disabled:opacity-50 text-xs"
                      data-testid={`comms-move-pick-${c.id}`}
                    >
                      <p className="font-medium text-slate-900 truncate">{c.title || "Untitled case"}</p>
                      <p className="text-[11px] text-slate-500 truncate">{c.property_address || "—"}</p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>


      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && !deleting && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="comms-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>
              {confirmDelete?.scope === "local" ? "Remove from ConveyTrack?" : "Delete this email?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.scope === "local" ? (
                <>This will hide <span className="font-medium text-slate-900">{confirmDelete?.item?.subject}</span> from this case's Communications feed. The original will stay in your Outlook untouched and will reappear if you click "Sync from Outlook" again.</>
              ) : (
                <>This will remove <span className="font-medium text-slate-900">{confirmDelete?.item?.subject}</span> from ConveyTrack{confirmDelete?.item?.graph_message_id ? " and also move it to your Outlook Deleted Items folder" : ""}. This cannot be undone.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={async () => {
                if (!confirmDelete) return;
                const { item, scope } = confirmDelete;
                setDeleting(true);
                try {
                  const qs = scope === "local" ? "?from_outlook=false" : "";
                  const { data } = await API.delete(`/cases/${caseId}/communications/${item.id}${qs}`);
                  setItems((arr) => arr.filter((m) => m.id !== item.id));
                  if (scope === "local") {
                    toast.success("Removed from ConveyTrack (your Outlook copy is untouched)");
                  } else if (data.deleted_from_outlook) {
                    toast.success("Email deleted from ConveyTrack and your Outlook");
                  } else if (data.outlook_error) {
                    toast.success("Removed from ConveyTrack (Outlook delete failed — check Outlook)");
                  } else {
                    toast.success("Email deleted");
                  }
                  setConfirmDelete(null);
                } catch (err) {
                  toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
                } finally {
                  setDeleting(false);
                }
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="comms-delete-confirm-btn"
            >
              {deleting ? "Deleting…" : (confirmDelete?.scope === "local" ? "Remove" : "Delete email")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
